/**
 * What we ask a customer's permission for, and what they have said —
 * `GET /v1/privacy/consents` (backend `#133`, `#523`).
 *
 * # Transcribed from the contract, not invented
 *
 * Every field here comes from `ConsentItem` in `api/openapi.yaml`. That
 * distinction is load-bearing in this repo twice over: `WalletApi`'s other read
 * methods describe endpoints that do not exist (§9), and `#25` typed a field
 * from reasoning rather than from the published shape and would have failed on
 * first contact in both directions. The hash field is `text_sha256`. There is a
 * `content_hash` in that same document, on a different schema, and it is not
 * this.
 *
 * # This module renders a legal record, which changes the failure rule
 *
 * `accounts.ts` refuses an account it cannot read rather than substituting a
 * default, because a defaulted account state is a lie about somebody's money.
 * The same rule applies here for a sharper reason: these rows are the Art. 7(1)
 * evidence that a named person accepted a named version of a named text. A
 * field guessed at on the way to the screen is a screen that misreports what
 * somebody agreed to.
 *
 * So every field the contract marks required must arrive, with the right type,
 * or the item is unreadable and the whole read fails. Note that this is
 * STRICTER than `decodeAccount`, which coerces `can_receive` with `=== true` on
 * the grounds that a missing flag has a safe reading — do not advertise the
 * account. No field here has a safe reading in both directions:
 *
 *   `needs_answer`  absent and read as false hides an obligation from the
 *                   customer; absent and read as true invents one. Both
 *                   misinform, so neither is a default worth having.
 *   `granted`       the same, one level down, and it is the answer itself.
 *   `choice`        absent and read as true would offer a control over
 *                   something the customer has no say in.
 *
 * # And the three the contract goes out of its way to warn about
 *
 *   render from `needs_answer`, NOT from `granted`. It is true when they have
 *   never answered AND when the wording has moved since they did — "a grant
 *   against version 1 is not a grant against version 2, which is the whole point
 *   of versioning the text".
 *
 *   `choice: false` is a DISCLOSURE. Something done on a basis the customer's
 *   consent has no bearing on, such as the terms of service. "Render an
 *   acknowledgement, never a toggle: offering a control that does not exist is
 *   worse than offering none, because turning it off documents that we ignored
 *   them."
 *
 *   `body` is shown verbatim. It is the text that was hashed, and nothing here
 *   trims, wraps, summarises or translates it.
 */

import { ApiError, isNotACustomer } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

export const CONSENTS_PATH = '/v1/privacy/consents'

/** `ConsentItem.lawful_basis`. Labels only — see `LAWFUL_BASIS_LABEL`. */
export const LAWFUL_BASES = [
  'consent',
  'contract',
  'legal_obligation',
  'legitimate_interest',
] as const
export type KnownLawfulBasis = (typeof LAWFUL_BASES)[number]

export interface Consent {
  /** What the processing is, e.g. `terms_of_service`, `marketing_email`. */
  readonly purpose: string
  /** The one line to render beside the control. */
  readonly summary: string
  /** The full wording, exactly as it must be shown. Never edited on the way here. */
  readonly body: string
  /** What makes a decision answer a specific wording rather than a purpose. */
  readonly version: number
  /**
   * The hash of `body`, echoed back on `PUT` so a cached screen cannot record
   * agreement to text nobody saw.
   *
   * Required to be a non-empty string and DELIBERATELY NOT checked against
   * `^[0-9a-f]{64}$`. That pattern is published on the registration schema's
   * copy of this field and not on `ConsentItem`, and asserting a constraint the
   * contract does not state is `#25` in the other direction — this time it would
   * blank the whole screen rather than fail a call.
   */
  readonly textSha256: string
  /**
   * False for a disclosure: processing on a basis the customer's consent has no
   * bearing on. An acknowledgement, never a toggle.
   */
  readonly choice: boolean
  /** Not narrowed to `KnownLawfulBasis` — see `decodeConsent`. */
  readonly lawfulBasis: string
  /** Whether we may do this right now. False when the wording moved since they answered. */
  readonly granted: boolean
  /** Whether they have said anything about the CURRENT wording. */
  readonly answered: boolean
  /** The field the UI is driven from. Unanswered, OR answered against older wording. */
  readonly needsAnswer: boolean
  /** Which version their most recent decision was made against. Optional in the contract. */
  readonly answeredVersion?: number
  readonly decidedAt?: string
}

/**
 * What the screen knows.
 *
 * `nothing_published` is separate from `ready` with an empty list, and that is
 * the whole point of this issue rather than a nicety. A deployment that has
 * published no wording "asks for nothing and records nothing" — correct
 * behaviour, and a sentence a customer can act on. Rendered as an empty list it
 * is a blank panel, which reads as a bug.
 *
 * `unregistered` and `unavailable` are separate again, for the reason `#22` and
 * `#101` made expensive: "nothing is being asked of you", "you have no wallet
 * yet" and "we could not look" are the same empty screen and mean entirely
 * different things about what the customer should do next.
 */
export type ConsentsState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready'
      readonly locale: string
      readonly consents: readonly Consent[]
    }
  /** The deployment has published no wording. NOT an error, and not an empty list. */
  | { readonly status: 'nothing_published'; readonly locale: string }
  /**
   * This deployment does not serve the route at all. NOT a failed read, and
   * emphatically not "nothing is being asked of you".
   *
   * The backend mounts consents, exports and erasure requests together or not
   * at all — `cmd/walletd/privacy.go` returns early unless object storage is
   * configured, "because a surface serving consents and 404ing exports is one a
   * client cannot tell from a broken deployment". Both overlays carry
   * `WALLET_OBJECT_STORE_ENDPOINT=` empty today, so this is what staging and
   * production actually answer (#124).
   *
   * It is separate from `unavailable` because the two are opposite instructions
   * to the customer: one is worth trying again and this one never will be.
   */
  | { readonly status: 'not_offered' }
  /** Signed in, no wallet yet — this endpoint refuses a caller with no customer. */
  | { readonly status: 'unregistered' }
  /** We could not find out. NOT the same as nothing being asked. */
  | { readonly status: 'unavailable'; readonly reason: string }

interface ConsentDTO {
  purpose?: unknown
  summary?: unknown
  body?: unknown
  version?: unknown
  text_sha256?: unknown
  choice?: unknown
  lawful_basis?: unknown
  granted?: unknown
  answered?: unknown
  needs_answer?: unknown
  answered_version?: unknown
  decided_at?: unknown
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

/**
 * Decode one item, or say why not.
 *
 * The reason is returned rather than thrown, and it is a sentence rather than a
 * code, because it is going to be shown to somebody: the screen's honest answer
 * to "what are you being asked to agree to" is "this app could not read it",
 * and that is more useful with the field named.
 */
export function decodeConsent(dto: ConsentDTO): Consent | string {
  const purpose = str(dto.purpose)
  if (purpose === undefined) return 'a consent arrived with no purpose'

  // Blank is the same failure as missing. A disclosure with no text is not a
  // disclosure, and rendering an empty panel under a heading would tell a
  // customer they had been shown something they had not.
  const body = str(dto.body)
  if (body === undefined) return `${purpose} arrived with no wording`

  const textSha256 = str(dto.text_sha256)
  if (textSha256 === undefined)
    return `${purpose} arrived with no content hash`

  // `summary` is required by the contract and may not be substituted with the
  // first line of `body`: they are separately authored, and a body's opening
  // sentence is not a summary of it.
  const summary = str(dto.summary)
  if (summary === undefined) return `${purpose} arrived with no summary`

  const lawfulBasis = str(dto.lawful_basis)
  if (lawfulBasis === undefined)
    return `${purpose} arrived with no lawful basis`

  if (
    typeof dto.version !== 'number' ||
    !Number.isInteger(dto.version) ||
    dto.version < 1
  ) {
    return `${purpose} arrived with no usable version (${String(dto.version)})`
  }

  for (const [key, value] of [
    ['choice', dto.choice],
    ['granted', dto.granted],
    ['answered', dto.answered],
    ['needs_answer', dto.needs_answer],
  ] as const) {
    if (typeof value !== 'boolean') {
      return `${purpose} arrived without ${key}, and there is no safe value to assume for it`
    }
  }

  return {
    purpose,
    summary,
    body,
    version: dto.version,
    textSha256,
    // NOT narrowed to the four the contract lists today, and that asymmetry is
    // deliberate — the same call `decodeAccount` makes about a currency code.
    // Nothing branches on the basis: `choice` is the field that decides whether
    // a control may exist, and it is checked above. So a basis this bundle has
    // not shipped is a LABEL it cannot pretty-print, and refusing on it would
    // cost the customer the wording they came to read on the day the backend
    // adds a fifth. `state` and `iban_status` are refused over there precisely
    // because something does branch on them.
    lawfulBasis,
    choice: dto.choice as boolean,
    granted: dto.granted as boolean,
    answered: dto.answered as boolean,
    needsAnswer: dto.needs_answer as boolean,
    ...(typeof dto.answered_version === 'number' &&
    Number.isInteger(dto.answered_version)
      ? { answeredVersion: dto.answered_version }
      : {}),
    ...(str(dto.decided_at) === undefined
      ? {}
      : { decidedAt: str(dto.decided_at) as string }),
  }
}

/**
 * Read what is being asked of this customer.
 *
 * Callers must already know they are talking about a customer. This route is
 * `session`-scoped AND its handler refuses `CustomerID == 0` with a **401**,
 * which `lib/api` turns into an `onUnauthenticated` notification and a fresh
 * round-trip to Ory. Calling it for a signed-in person who has not registered —
 * an entirely normal state — would set that off on every render. The screen
 * gates on `useOnboarding()` instead.
 *
 * That the sign-up path therefore cannot read this at all is not an oversight
 * here: it is `go-wallet#910`, and it is why `#117` remains open.
 */
export async function fetchConsents(api: WalletApi): Promise<ConsentsState> {
  let dto: { locale?: unknown; consents?: unknown }
  try {
    dto = await api.get<{ locale?: unknown; consents?: unknown }>(
      CONSENTS_PATH,
    )
  } catch (err) {
    // Today this route answers 401 rather than 403 for a caller with no
    // customer, so this branch is the one `go-wallet#910` would light up if it
    // is resolved by relaxing the route. Kept because `unregistered` is a state
    // the screen renders anyway, and one line is cheaper than the alternative
    // being a 401 the customer reads as "we could not look".
    if (isNotACustomer(err)) return { status: 'unregistered' }
    // A 404 IS NOT AN ANSWER FROM THIS HANDLER (#124).
    //
    // The contract publishes 200, 401, 426 and 500 for this route and no 404,
    // so a 404 cannot be a thing the handler decided — it can only mean the
    // route was never registered. That is a real deployment state rather than a
    // hypothetical: the whole privacy surface is mounted only when object
    // storage is configured, and `WALLET_OBJECT_STORE_ENDPOINT` is empty in both
    // overlays, so this is what our own deployments answer today.
    //
    // Reading it as `unavailable` was not wrong so much as untrue in the part
    // that matters to the person looking: `unavailable` carries a retry, and no
    // number of retries registers a route. Same rule as `NO_RETRY` in
    // `Welcome.tsx` (#75) — branch on what the answer means, and never offer an
    // action that cannot help.
    if (err instanceof ApiError && err.status === 404) {
      return { status: 'not_offered' }
    }
    return {
      status: 'unavailable',
      reason: err instanceof Error ? err.message : String(err),
    }
  }

  if (!Array.isArray(dto?.consents)) {
    return {
      status: 'unavailable',
      reason: 'the backend did not return a list of consents',
    }
  }

  // Required by the contract, and it says which language the wording is in — a
  // fact about a legal text, not a display preference. An absent one is not
  // worth losing the screen over, so it is reported as unknown rather than
  // defaulted to a language nobody chose.
  const locale = str(dto.locale) ?? ''

  const consents: Consent[] = []
  for (const raw of dto.consents as ConsentDTO[]) {
    const decoded = decodeConsent(raw ?? {})
    // One unreadable item fails the whole read rather than silently shortening
    // the list. A customer shown three of the four things being asked of them
    // has no way to know, and the hidden one is as likely as any to be the one
    // that matters.
    if (typeof decoded === 'string')
      return { status: 'unavailable', reason: decoded }
    consents.push(decoded)
  }

  return consents.length === 0
    ? { status: 'nothing_published', locale }
    : { status: 'ready', locale, consents }
}

/** Human labels. Ours. An unknown basis renders as its own code rather than as a blank. */
export const LAWFUL_BASIS_LABEL: Record<string, string> = {
  consent: 'your consent',
  contract: 'our contract with you',
  legal_obligation: 'a legal obligation',
  legitimate_interest: 'a legitimate interest',
}

export function lawfulBasisLabel(basis: string): string {
  return LAWFUL_BASIS_LABEL[basis] ?? basis
}

/**
 * How one purpose reads, in one line.
 *
 * DRIVEN FROM `needsAnswer`, NEVER FROM `granted` — the contract names this
 * explicitly and it is the easiest thing on this screen to get quietly wrong.
 * `granted` is false both when somebody declined and when the wording moved
 * under a grant they *did* give, so a screen built on it tells a customer they
 * have never engaged with something they engaged with last year.
 */
export function standing(c: Consent): {
  label: string
  tone: 'ask' | 'yes' | 'no' | 'ack'
} {
  if (!c.choice) {
    // A disclosure. There is no "declined" here — the backend answers a refusal
    // to accept one with 409 `not_a_choice` — so the two states are "we have
    // your acknowledgement of this wording" and "we do not".
    return c.needsAnswer
      ? { label: 'Needs your acknowledgement', tone: 'ask' }
      : { label: 'Acknowledged', tone: 'ack' }
  }
  if (c.needsAnswer) return { label: 'Needs your answer', tone: 'ask' }
  return c.granted
    ? { label: 'You allowed this', tone: 'yes' }
    : { label: 'You declined this', tone: 'no' }
}

/**
 * When a decision was taken, as a person reads it — "2 January 2026".
 *
 * Takes its input explicitly and holds no ambient clock, so it is
 * table-testable (`CLAUDE.md` Part III's rule for the backend's pure decision
 * functions, and the same one here).
 *
 * **`null` rather than a fallback string when the instant cannot be read.** The
 * caller drops the clause entirely rather than printing `Invalid Date` beside a
 * legal record — an unreadable date is not a date, and a sentence saying when
 * somebody agreed to something is the wrong place to be approximately right.
 */
export function decisionDate(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
}

// --- The write half (`PUT /v1/privacy/consents`, `go-wallet-web#126`) --------
//
// The read half above renders what is being asked. This records the answer.
//
// Three properties of the contract shape everything below, and none of them is
// a preference:
//
//   1. `version` and `text_sha256` come from the GET THAT WAS RENDERED and are
//      echoed back unchanged. They are what makes a decision an answer to a
//      specific wording rather than to a purpose, and a client that omits them
//      can record agreement to text the customer never saw. A stale pair is
//      answered `409 text_changed` with NO row written — re-render and ask
//      again rather than retrying.
//   2. `granted` is REQUIRED. Absent is not false: a client that omits it would
//      record a withdrawal nobody made.
//   3. `action` is the affirmative act (Art. 7(2)). "They agreed" is not a
//      record; "they ticked an unticked box" is. So it is taken from the
//      control the customer actually used, never defaulted.
//
// Nothing here composes wording. Every byte a customer is shown comes out of
// the GET and is rendered verbatim, and this module echoes the hash of those
// bytes back.

/** The affirmative act, from the contract's enum. Never inferred. */
export type ConsentAction =
  | 'checkbox_ticked'
  | 'checkbox_cleared'
  | 'button_pressed'
  | 'form_submitted'

/**
 * The surface. This is the web app, so it is `web` and nothing else — the
 * contract's other values name the native apps, which do not run this code.
 */
export const CONSENT_CHANNEL = 'web' as const

/**
 * What the screen learns from an attempt.
 *
 * `text_changed` and `not_a_choice` are separate from `rejected` for the same
 * reason `nothing_published` is separate from `ready` with an empty list: they
 * tell the customer different things about what to do next. A stale pair means
 * re-read and ask again; refusing a disclosure is not a thing the law offers.
 */
export type RecordConsentResult =
  /** Written. `recordedAt` is the backend's instant, not this client's. */
  | { readonly status: 'recorded'; readonly granted: boolean; readonly recordedAt: string }
  /**
   * The wording moved between the render and the answer. NO row was written.
   * The screen must re-read and ask again against the new text — retrying this
   * same decision would answer a version the customer never saw.
   */
  | { readonly status: 'text_changed' }
  /**
   * A disclosure was refused. `granted: false` on a purpose whose `choice` is
   * false is answered `409 not_a_choice`: there is no lawful "no" to record,
   * because the processing does not rest on their consent.
   */
  | { readonly status: 'not_a_choice' }
  /** No such purpose, or it has no published wording. */
  | { readonly status: 'unknown_purpose' }
  /** The route is not mounted in this deployment (#124). Not retryable. */
  | { readonly status: 'not_offered' }
  /** Signed in, but no wallet yet — the same state the read reports. */
  | { readonly status: 'unregistered' }
  /** Anything else. Retryable, and the reason is for us rather than for them. */
  | { readonly status: 'unavailable'; readonly reason: string }

interface DecisionResponseDTO {
  purpose?: unknown
  granted?: unknown
  version?: unknown
  recorded_at?: unknown
}

/**
 * Record one decision against the wording the customer was actually shown.
 *
 * Takes the whole `Consent` rather than a purpose string so the version and
 * hash cannot be supplied from anywhere except the item that was rendered.
 * Passing them separately would make it possible — and eventually inevitable —
 * for a caller to pair a purpose with a hash from a different read.
 */
export async function recordConsent(
  api: WalletApi,
  consent: Consent,
  granted: boolean,
  action: ConsentAction,
  opts: { readonly signal?: AbortSignal } = {},
): Promise<RecordConsentResult> {
  // Refused here rather than at the backend, because this one is knowable from
  // what we already rendered and the customer gets a straight answer instead of
  // a round trip that ends in a 409 they cannot act on.
  if (!consent.choice && !granted) return { status: 'not_a_choice' }

  let dto: DecisionResponseDTO
  try {
    dto = await api.put<DecisionResponseDTO>(
      CONSENTS_PATH,
      {
        purpose: consent.purpose,
        granted,
        action,
        channel: CONSENT_CHANNEL,
        // Sent for a toggle too, where the contract makes them optional. The
        // customer answered a screen showing one specific text either way, and
        // recording WHICH text costs nothing here and is unrecoverable later.
        version: consent.version,
        text_sha256: consent.textSha256,
      },
      {
        // This route publishes no Idempotency-Key. The version/hash pair is the
        // replay guard: a stale pair is refused with no row written.
        replayGuardedBy: 'version + text_sha256 (409 text_changed, no row written)',
        ...(opts.signal === undefined ? {} : { signal: opts.signal }),
      },
    )
  } catch (err) {
    if (isNotACustomer(err)) return { status: 'unregistered' }
    if (err instanceof ApiError) {
      // A 404 from this route is the handler answering "no such purpose". That
      // is unlike the GET, where a 404 cannot be the handler's answer at all
      // because the contract publishes none — the difference is in the contract
      // rather than in the status code, which is why it is read per route.
      if (err.status === 404) return { status: 'unknown_purpose' }
      if (err.status === 409) {
        if (err.code === 'not_a_choice') return { status: 'not_a_choice' }
        if (err.code === 'text_changed') return { status: 'text_changed' }
      }
      if (err.status === 400 && err.code === 'text_changed') {
        return { status: 'text_changed' }
      }
    }
    return {
      status: 'unavailable',
      reason: err instanceof Error ? err.message : String(err),
    }
  }

  // The backend's own instant, and the screen shows it rather than "just now".
  // A missing one is not worth losing a recorded decision over, so it reports
  // as unavailable-to-display rather than as a failed write.
  const recordedAt = str(dto?.recorded_at) ?? ''
  return {
    status: 'recorded',
    granted: typeof dto?.granted === 'boolean' ? dto.granted : granted,
    recordedAt,
  }
}
